import React, { Fragment, Component } from 'react'
import i18n from 'i18next'
import { reaction } from 'mobx'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import ChartSelector from '../ChartSelector'

import DebugInfo from '../DebugInfo'
import colours from '../Chart/colours'
import Chart from '../Chart/index'
import ReportPageTitle from '../ReportPageTitle'
import { getOptionTitle, getQuestionTitle } from '../../../../../utils/QuestionHelper'
import { sortedIndexArray, getSortDirection } from '../../../../../utils/helpers'
import Panel from '../../../../../UniformComponents/Panel'
import QuestionFooter from '../QuestionFooter'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import QuestionEdit from '../QuestionEdit'

@observer
class NumberForm extends Component {
    constructor(props) {
        super(props)
        const {
            reportStore, question, dataKey, store,
        } = props
        const { survey } = store
        this.isMaxbiReport = survey.isMaxbi()
        this.showChart = this.isMaxbiReport ? reportStore.showChart(question) : true
        this.data = reportStore.reportDataV2.questions[props.dataKey]
        const locales = {
            locale: reportStore.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale: question.metadata.initialLocale,
        }
        this.categories = question.optionSet.options.map((o) => (getOptionTitle({
            langs: o.shortTitle,
            locale: locales.locale,
            defaultLocale: locales.defaultLocale,
            initialLocale: locales.initialLocale,
        })))
        this.state = {
            updated: new Date().getTime(),
            sorting: {
                direction: undefined,
                sortBy: undefined,
                defaultSorting: Array.from(Array(this.categories.length).keys()),
                sortedIndexArray: Array.from(Array(this.categories.length).keys()),
            },
            chartType: R.pathOr('column', ['settings', 'report', 'graphType'], question),
        }
        this.chartKey = uuidv4()
        this.chartColours = [colours.getSerieColour('survey'), colours.getSerieColour('comparison')]

        reaction(
            () => reportStore.reportDataFetched,
            () => {
                this.categories = reportStore.reportDataV2.questions[dataKey].yOptions.map((option) => option.label)
                this.data = reportStore.reportDataV2.questions[props.dataKey]
                this.chartKey = uuidv4()
                this.setState({
                    updated: props.reportStore.reportDataFetched,
                    sorting: {
                        direction: undefined,
                        sortBy: undefined,
                        defaultSorting: Array.from(Array(this.categories.length).keys()),
                        sortedIndexArray: Array.from(Array(this.categories.length).keys()),
                    },
                })
            },
        )

        reportStore.questionSorting[dataKey] = this.getQuestionSorting
    }

    componentWillUnmount() {
        const { reportStore, dataKey } = this.props
        Reflect.deleteProperty(reportStore.questionSorting, dataKey)
    }

    getQuestionSorting = () => {
        const { sorting } = this.state
        return sorting.sortedIndexArray
    }

    generateDataTable = () => {
        const { sorting } = this.state
        const { reportStore, dataKey, question } = this.props
        const { currentCurrency, currencySign } = reportStore
        const data = reportStore.reportDataV2.questions[dataKey]

        const comparison = Array.from(reportStore.yoyComparisons)
            .filter(([id]) => R.pathOr(false, ['comparison', id.toUpperCase()])(data))
            .map(([id, { label }]) => ({
                label: reportStore.comparisonTitle(label),
                path: `comparison.${id.toUpperCase()}`,
                type: 'pct',
                colorKey: 'comparison',
                key: `survey_${label}`,
            }))

        const cols = this.isMaxbiReport
            ? [
                {
                    label: 'Option Name',
                    class: 'tleft',
                    type: 'optionTitle',
                },
                {
                    label: i18n.t('report_question_response'),
                    path: 'pctOfResponses',
                    type: 'pct',
                },
            ]
            : [
                {
                    label: 'Option Name',
                    class: 'tleft',
                    type: 'optionTitle',
                },
                {
                    label: i18n.t('report_question_responses'),
                    path: 'numberOfResponses',
                    type: 'val',
                },
                {
                    label: reportStore.surveyTitle(),
                    path: 'pctOfResponses',
                    type: 'pct',
                },
                ...comparison,
                // ...averages,
            ]

        this.cols = this.isMaxbiReport ? cols.slice(1) : cols.slice(2)

        return (
            <div style={{ overflowX: 'auto' }}>
                <table
                    className="datatable question tablesorter tablesorter-default"
                    style={{
                        width: '100%',
                    }}
                >
                    <thead>
                        <tr className="col tablesorter-headerRow">
                            {cols.map((col) => (
                                <th style={{ cursor: 'pointer' }} className={`${col.class || ''} tablesorter-header`} onClick={() => this.sortBy(col)}>
                                    <div className="tablesorter-header-inner">
                                        {col.label}
                                        {this.sortIcon(col)}
                                    </div>
                                </th>
                            ))}
                            <td className="tlast" />
                        </tr>
                    </thead>
                    <tbody>
                        {sorting.sortedIndexArray.map((index) => {
                            const option = this.categories[index]
                            return (
                                <tr className="col" key={`option_${option}`}>
                                    {cols.map((col) => this.colMapper(col, data, option, index))}
                                    <td className="tlast" />
                                </tr>
                            )
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="col">
                            <th>
                                {i18n.t('report_total')}
                                :
                            </th>
                            {!this.isMaxbiReport && <td />}
                            <td>
                                {question.settings.isCurrency
                                    ? `${currencySign[currentCurrency]} ${reportStore.convertCurrency(R.pathOr(0, ['aggregate', 'average'])(data)).toFixed(0)}`
                                    : R.pathOr(0, ['aggregate', 'total'])(data).toFixed(2)}
                            </td>
                            {!this.isMaxbiReport ? Object.keys(R.pathOr({}, ['comparison'], data)).map((k) => (
                                <td>
                                    {question.settings.isCurrency
                                        ? `${currencySign[currentCurrency]} ${reportStore.convertCurrency(R.pathOr(0, ['aggregate', 'comparison', k, 0], data))}`
                                        : R.pathOr(0, ['aggregate', 'comparison', k, 0], data).toFixed(2)}
                                </td>
                            )) : null}
                            <td className="tlast" />
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    colMapper = ({ path, type }, data, option, index) => {
        const { question, reportStore } = this.props
        const { currentCurrency, currencySign } = reportStore
        let col = null
        if (type === 'pct') {
            if (question.settings.isCurrency) {
                col = <td>{`${currencySign[currentCurrency]} ${reportStore.convertCurrency(R.pathOr(0, [...path.split('.'), index])(data)).toFixed(0)}`}</td>
            } else {
                col = <td>{R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}</td>
            }
        } else if (type === 'optionTitle') {
            col = <th>{option}</th>
        } else if (type === 'val') {
            col = <td>{R.pathOr(0, [...path.split('.'), index])(data)}</td>
        }
        return col
    }

    sortBy = (col) => {
        const sorting = R.clone(this.state.sorting)
        const {
            reportStore, question, store, dataKey,
        } = this.props
        const { survey } = store

        const oldSortBy = sorting.sortBy

        const locales = {
            locale: reportStore.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale: question.metadata.initialLocale,
        }
        const { values, sortBy } = reportStore.sortDetails(
            dataKey, col, question.optionSet.options, locales,
        )

        sorting.sortBy = sortBy
        const indexArray = sortedIndexArray(values)
        sorting.direction = getSortDirection(sorting.sortBy === oldSortBy, sorting.direction)

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = indexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = indexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }
        this.setState({
            sorting,
        })
    }

    sortIcon = ({ path }) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (direction && ((!path && sortBy === 'label') || path === sortBy)) {
            const iconClass = direction === 'ASC' ? 'fal fa-long-arrow-up' : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    getChartCategorySet = () => {
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        console.log(
            'sorted categoryMap',
            sortedIndexArray.map((sortedIndex) => this.categories[sortedIndex]),
        )
        return sortedIndexArray.map((sortedIndex) => this.categories[sortedIndex])
    }

    getChartCategories = () => {
        const { categories } = this
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        console.log(
            'sorted categories',
            sortedIndexArray.map((sortedIndex) => categories[sortedIndex]),
        )
        return sortedIndexArray.map((sortedIndex) => categories[sortedIndex])
    }

    render() {
        const {
            question,
            store,
            pageId,
            reportStore,
            reportFilter,
            dataKey,
        } = this.props
        const { survey } = store
        const { chartType } = this.state
        const data = R.or(reportStore.reportDataV2.questions[dataKey], {})
        return (
            <Panel cssClass="question">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={getQuestionTitle({
                                content: question.content.shortTitle,
                                formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            h3={getQuestionTitle({
                                content: question.content.longTtile,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit
                            reportStore={reportStore}
                            pageId={pageId}
                        />
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                <div id={`questionData_${question.pageQuestionId}`}>
                    {this.generateDataTable()}
                </div>
                {this.showChart && this.categories.length > 0 && (
                    <Fragment>
                        <ChartSelector
                            chartType={chartType}
                            onChange={(chartType) => { this.setState({ chartType }) }}
                        />
                        <Chart
                            colors={this.chartColours}
                            key={this.chartKey}
                            survey={survey}
                            data={data}
                            isCurrency={question.settings.isCurrency}
                            currentCurrency={reportStore.currentCurrency}
                            dataKey={dataKey}
                            pointEvent={this.chartFilter}
                            question={question}
                            reportFilter={reportFilter}
                            series={this.cols}
                            sort={`${this.state.sorting.sortBy}_${this.state.sorting.direction}`}
                            sortedIndexArray={this.state.sorting.sortedIndexArray}
                            pageQuestionId={question.pageQuestionId}
                            id={question.pageQuestionId}
                            categorySet={this.getChartCategorySet()}
                            chartType={chartType}
                            questionTypeCode={question.questionTypeCode}
                            kpiType={question.metadata.kpiType}
                            categories={this.getChartCategories()}
                            reportStore={reportStore}
                        />
                        <QuestionFooter
                            dataKey={dataKey}
                            reportStore={reportStore}
                        />
                    </Fragment>
                )}
            </Panel>
        )
    }
}

export default NumberForm
